import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { RestockFromSupplierModal } from './RestockFromSupplierModal';
import { QuickSupplierModal } from '../../pos/components/QuickSupplierModal';
import axiosInstance from '../../../lib/axios';
import type {
  ProductSupplierPrice,
  Supplier,
  InventoryBatch,
  CreateProductSupplierPriceDto,
  UpdateProductSupplierPriceDto,
} from '../../../types';
import { palette } from '../../../theme/colors';
import { useCanViewFinancials } from '../../../hooks/usePermissions';

// Special option ID for creating a new supplier
const CREATE_SUPPLIER_ID = '__create__';

// Default colors for QuickSupplierModal when used outside POS context
const defaultQuickModalColors = {
  bg: palette.background,
  surface: palette.surface,
  surfaceLight: palette.surfaceElevated,
  border: palette.border,
  textPrimary: palette.textPrimary,
  textSecondary: palette.textSecondary,
  textMuted: palette.textMuted,
  accent: palette.primary,
  accentLight: palette.primaryLight,
};

interface ProductSuppliersTabProps {
  productId: string;
  productName: string;
  defaultCostPrice?: number;
  currentStock?: number;
}

interface AddSupplierModalProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  existingSupplierIds: string[];
  editingPrice?: ProductSupplierPrice | null;
}

const AddSupplierModal = ({
  open,
  onClose,
  productId,
  existingSupplierIds,
  editingPrice,
}: AddSupplierModalProps) => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const [supplierId, setSupplierId] = useState(editingPrice?.supplierId || '');
  const [unitCost, setUnitCost] = useState(editingPrice?.unitCost?.toString() || '');
  const [minimumQuantity, setMinimumQuantity] = useState(
    editingPrice?.minimumQuantity?.toString() || ''
  );
  const [currency, setCurrency] = useState(editingPrice?.currency || 'DZD');
  const [isPreferred, setIsPreferred] = useState(editingPrice?.isPreferred || false);
  const [quickSupplierModalOpen, setQuickSupplierModalOpen] = useState(false);

  // Fetch available suppliers
  const { data: suppliers, isLoading: loadingSuppliers } = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: async () => {
      const response = await axiosInstance.get('/suppliers', {
        params: { isActive: true, limit: 100 },
      });
      return response.data.items || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateProductSupplierPriceDto) => {
      const response = await axiosInstance.post(
        `/products/${productId}/supplier-prices`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-supplier-prices', productId] });
      showMessage('Fournisseur ajouté avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de l\'ajout';
      showMessage(message, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateProductSupplierPriceDto) => {
      const response = await axiosInstance.patch(
        `/products/${productId}/supplier-prices/${editingPrice?.id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-supplier-prices', productId] });
      showMessage('Prix mis à jour avec succès', 'success');
      onClose();
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      showMessage(message, 'error');
    },
  });

  const handleSubmit = () => {
    const data = {
      supplierId,
      unitCost: parseFloat(unitCost),
      minimumQuantity: minimumQuantity ? parseInt(minimumQuantity) : undefined,
      currency,
      isPreferred,
    };

    if (editingPrice) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Filter out already linked suppliers (except when editing)
  const availableSuppliers = (suppliers || []).filter(
    (s: Supplier) => !existingSupplierIds.includes(s.id) || s.id === editingPrice?.supplierId
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingPrice ? 'Modifier le prix fournisseur' : 'Ajouter un fournisseur'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth disabled={!!editingPrice || loadingSuppliers}>
            <InputLabel>Fournisseur</InputLabel>
            <Select
              value={supplierId}
              onChange={(e) => {
                const value = e.target.value;
                if (value === CREATE_SUPPLIER_ID) {
                  setQuickSupplierModalOpen(true);
                } else {
                  setSupplierId(value);
                }
              }}
              label="Fournisseur"
            >
              {/* Create New Supplier option */}
              <MenuItem
                value={CREATE_SUPPLIER_ID}
                sx={{
                  color: palette.primary,
                  fontWeight: 600,
                  borderBottom: `1px solid ${palette.border}`,
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: palette.primaryMuted,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                  Créer un nouveau fournisseur
                </Box>
              </MenuItem>
              {/* Existing suppliers */}
              {availableSuppliers.map((supplier: Supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name} ({supplier.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Prix unitaire (coût)"
            type="number"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            fullWidth
            required
            InputProps={{
              endAdornment: <Typography color="text.secondary">{currency}</Typography>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />

          <TextField
            label="Quantité minimum"
            type="number"
            value={minimumQuantity}
            onChange={(e) => setMinimumQuantity(e.target.value)}
            fullWidth
            helperText="Quantité minimum pour ce prix (optionnel)"
            inputProps={{ min: 1 }}
          />

          <FormControl fullWidth>
            <InputLabel>Devise</InputLabel>
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              label="Devise"
            >
              <MenuItem value="DZD">DZD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={isPreferred}
                onChange={(e) => setIsPreferred(e.target.checked)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon sx={{ fontSize: 18, color: isPreferred ? 'warning.main' : 'text.secondary' }} />
                Fournisseur préféré
              </Box>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !supplierId || !unitCost}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {editingPrice ? 'Mettre à jour' : 'Ajouter'}
        </Button>
      </DialogActions>

      {/* Quick Supplier Creation Modal */}
      <QuickSupplierModal
        open={quickSupplierModalOpen}
        onClose={() => setQuickSupplierModalOpen(false)}
        onSupplierCreated={(newSupplier) => {
          // Auto-select the newly created supplier
          setSupplierId(newSupplier.id);
          // Invalidate suppliers cache to refresh the dropdown
          queryClient.invalidateQueries({ queryKey: ['suppliers'] });
          queryClient.invalidateQueries({ queryKey: ['suppliers', 'active'] });
          showMessage('Fournisseur créé avec succès', 'success');
          setQuickSupplierModalOpen(false);
        }}
        colors={defaultQuickModalColors}
      />
    </Dialog>
  );
};

interface SupplierCardProps {
  supplierPrice: ProductSupplierPrice;
  productId: string;
  onEdit: () => void;
  onRestock: () => void;
  canViewCosts?: boolean;
}

const SupplierCard = ({ supplierPrice, productId, onEdit, onRestock, canViewCosts = true }: SupplierCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.delete(
        `/products/${productId}/supplier-prices/${supplierPrice.id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-supplier-prices', productId] });
      showMessage('Lien fournisseur supprimé', 'success');
    },
    onError: () => {
      showMessage('Erreur lors de la suppression', 'error');
    },
  });

  const handleDelete = () => {
    if (window.confirm('Voulez-vous vraiment supprimer ce lien fournisseur ?')) {
      deleteMutation.mutate();
    }
  };

  const supplier = supplierPrice.supplier;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: palette.primaryMuted,
                color: palette.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BusinessIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {supplier?.name || 'Fournisseur inconnu'}
                </Typography>
                {supplierPrice.isPreferred && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 14 }} />}
                    label="Préféré"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 22 }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {supplier?.code}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<LocalShippingIcon />}
              onClick={onRestock}
              sx={{ mr: 1 }}
            >
              Réappro
            </Button>
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
          {canViewCosts && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Prix actuel
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {supplierPrice.unitCost.toLocaleString()} {supplierPrice.currency}
              </Typography>
            </Box>
          )}
          {supplierPrice.lastPurchaseDate && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Dernier achat
              </Typography>
              <Typography variant="body2">
                {new Date(supplierPrice.lastPurchaseDate).toLocaleDateString('fr-FR')}
                {supplierPrice.lastPurchaseQuantity && (
                  <Typography component="span" color="text.secondary">
                    {' '}({supplierPrice.lastPurchaseQuantity} unités)
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
          {supplierPrice.minimumQuantity && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Qté min.
              </Typography>
              <Typography variant="body2">{supplierPrice.minimumQuantity}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none' }}
          >
            {expanded ? 'Masquer historique' : 'Voir historique'}
          </Button>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Historique des prix
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (Fonctionnalité à venir - affichera l'historique des prix d'achat)
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export const ProductSuppliersTab = ({
  productId,
  productName,
  defaultCostPrice,
  currentStock = 0,
}: ProductSuppliersTabProps) => {
  const canViewCosts = useCanViewFinancials();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState<ProductSupplierPrice | null>(null);
  const [restockModalOpen, setRestockModalOpen] = useState(false);
  const [restockSupplierId, setRestockSupplierId] = useState<string | undefined>();
  const [restockSupplierName, setRestockSupplierName] = useState<string | undefined>();
  const [restockSuggestedPrice, setRestockSuggestedPrice] = useState<number | undefined>();

  // Fetch supplier prices for this product
  const { data: supplierPrices, isLoading: loadingPrices } = useQuery({
    queryKey: ['product-supplier-prices', productId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/products/${productId}/supplier-prices`);
      return response.data as ProductSupplierPrice[];
    },
    enabled: !!productId,
  });

  // Fetch inventory batches for this product
  const { data: batches, isLoading: loadingBatches } = useQuery({
    queryKey: ['product-batches', productId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/products/${productId}/batches`);
      return response.data as InventoryBatch[];
    },
    enabled: !!productId,
  });

  const handleAddClick = () => {
    setEditingPrice(null);
    setModalOpen(true);
  };

  const handleEditClick = (price: ProductSupplierPrice) => {
    setEditingPrice(price);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingPrice(null);
  };

  // Global restock (no supplier pre-selected)
  const handleGlobalRestockClick = () => {
    setRestockSupplierId(undefined);
    setRestockSupplierName(undefined);
    setRestockSuggestedPrice(undefined);
    setRestockModalOpen(true);
  };

  // Restock for a specific supplier
  const handleSupplierRestockClick = (price: ProductSupplierPrice) => {
    setRestockSupplierId(price.supplierId);
    setRestockSupplierName(price.supplier?.name);
    setRestockSuggestedPrice(price.unitCost);
    setRestockModalOpen(true);
  };

  const handleRestockModalClose = () => {
    setRestockModalOpen(false);
    setRestockSupplierId(undefined);
    setRestockSupplierName(undefined);
    setRestockSuggestedPrice(undefined);
  };

  const existingSupplierIds = (supplierPrices || []).map((p) => p.supplierId);

  return (
    <Box>
      {/* Cost Price Header */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'action.hover',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {canViewCosts ? (
          <>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Prix d'achat actuel (coût moyen pondéré)
              </Typography>
              <Typography variant="h5" fontWeight={600} color="primary">
                {defaultCostPrice ? `${defaultCostPrice.toLocaleString()} DZD` : 'Non défini'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Calculé automatiquement à partir des réapprovisionnements
            </Typography>
          </>
        ) : (
          <Typography variant="subtitle2" color="text.secondary">
            Informations de coût masquées
          </Typography>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Suppliers Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Fournisseurs & Historique des prix</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              size="small"
            >
              Ajouter un fournisseur
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<LocalShippingIcon />}
              onClick={handleGlobalRestockClick}
              size="small"
            >
              Réapprovisionner
            </Button>
          </Box>
        </Box>

        {loadingPrices ? (
          <Box>
            <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={120} />
          </Box>
        ) : supplierPrices && supplierPrices.length > 0 ? (
          supplierPrices.map((price) => (
            <SupplierCard
              key={price.id}
              supplierPrice={price}
              productId={productId}
              onEdit={() => handleEditClick(price)}
              onRestock={() => handleSupplierRestockClick(price)}
              canViewCosts={canViewCosts}
            />
          ))
        ) : (
          <Alert severity="info">
            Aucun fournisseur lié à ce produit. Ajoutez un fournisseur pour suivre les prix d'achat.
          </Alert>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Batches Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Lots en stock
        </Typography>

        {loadingBatches ? (
          <Skeleton variant="rounded" height={200} />
        ) : batches && batches.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>N° Lot</TableCell>
                <TableCell>Fournisseur</TableCell>
                <TableCell>Emplacement</TableCell>
                <TableCell align="right">Qté restante</TableCell>
                {canViewCosts && <TableCell align="right">Coût unitaire</TableCell>}
                <TableCell>Reçu le</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch) => (
                <TableRow key={batch.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {batch.batchNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{batch.supplier?.name || '-'}</TableCell>
                  <TableCell>{batch.location?.name || '-'}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={batch.quantityRemaining}
                      size="small"
                      color={batch.quantityRemaining > 0 ? 'success' : 'default'}
                      variant={batch.quantityRemaining > 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  {canViewCosts && (
                    <TableCell align="right">
                      {batch.unitCost.toLocaleString()} DZD
                    </TableCell>
                  )}
                  <TableCell>
                    {new Date(batch.receivedDate).toLocaleDateString('fr-FR')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Alert severity="info">
            Aucun lot en stock. Les lots seront créés lors des réapprovisionnements avec suivi par lot activé.
          </Alert>
        )}
      </Box>

      {/* Add/Edit Supplier Modal */}
      <AddSupplierModal
        open={modalOpen}
        onClose={handleModalClose}
        productId={productId}
        existingSupplierIds={existingSupplierIds}
        editingPrice={editingPrice}
      />

      {/* Restock Modal */}
      <RestockFromSupplierModal
        open={restockModalOpen}
        onClose={handleRestockModalClose}
        productId={productId}
        productName={productName}
        supplierId={restockSupplierId}
        supplierName={restockSupplierName}
        suggestedPrice={restockSuggestedPrice}
        currentStock={currentStock}
      />
    </Box>
  );
};

export default ProductSuppliersTab;
